import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Referral,
  formatReferralType,
  getDaysSinceReferral,
} from "@/lib/api/referrals.ts";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Timer } from "lucide-react";

export const columns: ColumnDef<Referral>[] = [
  {
    accessorKey: "teen",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Teen" />
    ),
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => {
      // Extract teen name from the title (format: "Referral - Teen Name - Type")
      const title = row.original.title;
      const teenName = title.split(" - ")[1] || "Unknown";

      return <div className="font-medium">{teenName}</div>;
    },
  },
  {
    accessorKey: "referralDate",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    enableSorting: true,
    cell: ({ row }) => {
      const referralDate = row.original.metadata.referralDate;
      try {
        const date = new Date(referralDate);
        return format(date, "MMM dd, yyyy");
      } catch {
        return referralDate;
      }
    },
  },
  {
    accessorKey: "referralRecipient",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Recipient" />
    ),
    enableSorting: false,
    cell: ({ row }) => {
      const recipient = row.original.metadata.referralRecipient;
      return <div className="max-w-[200px] truncate">{recipient}</div>;
    },
  },
  {
    accessorKey: "referralSource",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Referral Source" />
    ),
    enableSorting: false,
    cell: ({ row }) => {
      const creator = row.original.createdBy.name;
      return <div className="max-w-[200px] truncate">{creator}</div>;
    },
  },
  {
    accessorKey: "postReferralEngagement",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Post referral engagement" />
    ),
    enableSorting: true,
    cell: ({ row }) => {
      const referralDate = row.original.metadata.referralDate;
      const days = getDaysSinceReferral(referralDate);

      // Color code based on days since referral
      const badgeColor =
        days <= 7 ? "default" : days <= 30 ? "secondary" : "destructive";

      return (
        <Badge variant={badgeColor} className="flex w-fit items-center gap-1">
          <Timer className="h-3 w-3" />
          {days} days
        </Badge>
      );
    },
  },
  {
    accessorKey: "referralType",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Type" />
    ),
    enableSorting: false,
    cell: ({ row }) => {
      const type = row.original.metadata.referralType;
      const formattedType = formatReferralType(type);

      return <Badge variant="outline">{formattedType}</Badge>;
    },
  },
  {
    accessorKey: "internalNote",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Notes" />
    ),
    enableSorting: false,
    cell: ({ row }) => {
      const note = row.original.metadata.internalNote;

      if (!note) {
        return <span className="text-muted-foreground">—</span>;
      }

      const truncated = note.length > 50 ? `${note.substring(0, 50)}...` : note;

      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="max-w-[200px] cursor-help truncate">
                {truncated}
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[300px]">
              <p className="whitespace-pre-wrap">{note}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    },
  },
];
